import { useEffect, useState, useMemo, useRef } from 'react';
import { useAtomValue } from 'jotai';
import { usePostHog } from 'posthog-js/react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { AlertCircle, ArrowRight, Plus, Pencil } from 'lucide-react';

import { BudgetV2 } from '@/components/business/budget-v2';

// Store
import { organizationDataAtom, campaignsDataAtom, campaignTaskTrackingAtom } from '@/store';

// Queries
import { conversionActionsQueryOptions, businessQueryOptions, excludedLocationsQueryOptions } from '@/lib/query-options';

// Components - Assets
import { SitelinkSection, type SitelinkSectionRef } from '@/components/campaign/assets/v0/sitelink-section';
import { CalloutSection, type CalloutSectionRef } from '@/components/campaign/assets/v0/callout-section';
import { PhoneNumberSection, type PhoneNumberSectionRef } from '@/components/campaign/assets/v0/phone-number-section';
import { LanguageSection } from '@/components/campaign/assets/v0/language-section';
import { StructuredSnippetSection, type StructuredSnippetSectionRef } from '@/components/campaign/assets/v0/structured-snippet-section';
import { PriceSection, type PriceSectionRef } from '@/components/campaign/assets/v0/price-section';
import { PromotionSection, type PromotionSectionRef } from '@/components/campaign/assets/v0/promotion-section';
import { ImageSection } from '@/components/campaign/assets/v0/image-section';
import { useTestMode } from '@/hooks/useTestMode';

// Components - Settings
import {
  CampaignBudgetView,
  LocationTargetingModeSection,
  ExcludedLocationsSection,
  TrackingTemplateView,
} from '@/components/campaign/settings';
import { CampaignLocationsSection } from '@/components/campaign/settings/campaign-locations-section';

// Components - Advanced
import { BidAdjustmentsView } from '@/components/campaign/settings/bid-adjustments-view';
import { AdScheduleView } from '@/components/campaign/settings/ad-schedule-view';
import { CampaignNegativeKeywordsView } from '@/components/campaign/settings/campaign-negative-keywords-view';
import { CampaignIpExclusionsView } from '@/components/campaign/settings/campaign-ip-exclusions-view';
import { ConversionTrackingTab } from '@/components/campaign/conversion-tracking-tab';
import { LandingPageTab } from '@/components/landing-pages/landing-page-tab';
import { BiddingStrategySelector } from '@/components/campaign/settings/bidding-strategy-selector';
import type { BiddingStrategyType } from '@/components/campaign/settings/bidding-strategy-selector';

// Hooks and utilities
import { useCampaignDraftFields } from '@/hooks/useCampaignDraft';
import type { CampaignData } from '@/lib/types';
import { APPROVAL_STATE, CAMPAIGN_TYPE_LABELS } from '@/lib/types';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { useSetCampaignType } from '@/hooks/useCampaignType';
import { classificationFailed, UNCLASSIFIED_GOAL } from '@/lib/campaign-classification';
import { getBudgetRecommendation } from '@/lib/apis';

export type CampaignTabType = 'overview' | 'assets' | 'budget' | 'negatives' | 'conversions' | 'targeting' | 'landing-page';

// The types a campaign can be set to (#6381); mirrors the backend's
// CLASSIFIABLE_GOALS in external_campaign_classifier.py.
const CAMPAIGN_TYPE_CHOICES = ['prospecting', 'brand', 'competitor', 'location', 'icp', 'hipo'] as const;

interface AdvancedSectionProps {
  campaign: CampaignData;
  activeTab: CampaignTabType;
  isExternal?: boolean;
  /** Assets sub-tab from the URL hash (`#assets/sitelinks`) and a setter that writes it back (#6171). */
  assetsSubTab?: string;
  onAssetsSubTabChange: (subId: string, replace?: boolean) => void;
}

export function AdvancedSection({
  campaign,
  activeTab,
  isExternal = false,
  assetsSubTab,
  onAssetsSubTabChange,
}: AdvancedSectionProps) {
  const organizationData = useAtomValue(organizationDataAtom);
  const taskTracking = useAtomValue(campaignTaskTrackingAtom);
  const allCampaigns = useAtomValue(campaignsDataAtom);
  const posthog = usePostHog();
  const isTestMode = useTestMode(); // Image extensions are pre-GA — gated on test mode (#4376)
  const [isBudgetModalOpen, setIsBudgetModalOpen] = useState(false);
  // Sub-tabs inside the Misc/Assets tab — one sub-tab per asset section. Count badges live on
  // the tab labels, and the Add button is hoisted into the tab row (dynamic per active tab).
  type MiscSubTab = 'sitelinks' | 'callouts' | 'snippets' | 'phone' | 'prices' | 'promotions' | 'images' | 'tracking';
  // The Assets sub-tab lives in the URL hash (`#assets/sitelinks`) so it's linkable and survives a
  // refresh (#6171). Default to Sitelinks, reflecting that default into the URL on first landing. An
  // invalid/stale value falls back to Sitelinks in the `activeSubTab` derivation below.
  const miscSubTab = (assetsSubTab ?? 'sitelinks') as MiscSubTab;
  useEffect(() => {
    if (activeTab === 'assets' && !assetsSubTab) onAssetsSubTabChange('sitelinks', true);
  }, [activeTab, assetsSubTab, onAssetsSubTabChange]);
  // Sub-tabs inside the Budget tab — #4505. Groups the existing sections so
  // the page isn't one long vertical scroll. No URL persistence (matches Misc).
  type BudgetSubTab = 'budget-bidding' | 'schedule' | 'bid-adjustments';
  const [budgetSubTab, setBudgetSubTab] = useState<BudgetSubTab>('budget-bidding');
  // Sub-tabs inside the Targeting tab — one sub-tab per section so the page isn't
  // one long vertical scroll. No URL persistence (matches Misc/Budget).
  type TargetingSubTab = 'locations' | 'excluded' | 'mode' | 'languages' | 'ip-exclusions';
  const [targetingSubTab, setTargetingSubTab] = useState<TargetingSubTab>('locations');
  // Refs let the hoisted Add button trigger each section's existing add-modal logic without
  // having to lift all of the modal state up to this component.
  const sitelinkRef = useRef<SitelinkSectionRef>(null);
  const calloutRef = useRef<CalloutSectionRef>(null);
  const snippetRef = useRef<StructuredSnippetSectionRef>(null);
  const phoneRef = useRef<PhoneNumberSectionRef>(null);
  const priceRef = useRef<PriceSectionRef>(null);
  const promotionRef = useRef<PromotionSectionRef>(null);
  const { draft, updateDraft } = useCampaignDraftFields();
  const setCampaignType = useSetCampaignType(campaign);
  // The strict warning (#6381 chunk F): holds the picked type until confirmed.
  const [pendingTypeChange, setPendingTypeChange] = useState<string | null>(null);
  const handleTypeSelected = (goal: string) => {
    const alreadyTyped = !!campaign.goal && campaign.goal !== 'external';
    if (alreadyTyped) {
      setPendingTypeChange(goal);
    } else {
      // First confirmation on an external campaign: the adoption act, no warning.
      setCampaignType.mutate(goal);
    }
  };
  const { data: conversionActions } = useQuery(conversionActionsQueryOptions());
  // #5466: campaign's excluded locations, to badge them in the targeting picker.
  const { data: excludedLocationsData } = useQuery(excludedLocationsQueryOptions(campaign.id));
  const excludedCriteriaIds = useMemo(
    () => new Set((excludedLocationsData?.locations ?? []).map((l) => l.criteria_id)),
    [excludedLocationsData],
  );
  // Only enable Target CPA when at least one conversion action is active (not REMOVED)
  const conversionTrackingEnabled = !!conversionActions?.conversion_actions?.some(
    (action) => action.status !== 'REMOVED'
  );

  // Fetch business data for platform_budgets
  const { data: business } = useQuery({
    ...businessQueryOptions(organizationData?.id || 0),
    enabled: !!organizationData?.id,
  });

  // Compute remaining daily budget from platform allocation
  const remainingDailyBudget = useMemo(() => {
    const googlePlatform = business?.platform_budgets?.find(b => b.platform === 'Google');
    if (!googlePlatform) return null;
    const platformMonthly = googlePlatform.amount;
    const otherCampaignsMonthly = allCampaigns
      .filter(c => c.id !== campaign.id)
      .reduce((sum, c) => sum + (c.budget || 0) * 30, 0);
    return Math.floor((platformMonthly - otherCampaignsMonthly) / 30);
  }, [business?.platform_budgets, allCampaigns, campaign.id]);

  // Track page view
  useEffect(() => {
    if (campaign) {
      posthog?.capture('campaign_advanced_viewed', {
        campaign_id: campaign.id,
        campaign_name: campaign.name,
      });
    }
  }, [campaign?.id, posthog]);

  if (!campaign || !draft) return null;

  // Ensure campaign has the required asset arrays
  const campaignWithAssets = {
    ...campaign,
    site_links: campaign.site_links || [],
    callouts: campaign.callouts || [],
    structured_snippets: campaign.structured_snippets || [],
  };

  return (
    <div className="space-y-6">
      {/* === OVERVIEW TAB ===
              Two-column metadata grid (Name + Website URL on the left, Type + Created On
              on the right), then full-width Campaign Description and Tracking Template
              stacked below. Inlines the field rendering instead of using CampaignInfoView,
              which is still used by settings-dashboard with its default single-column shape. */}
      {activeTab === 'overview' && (() => {
        const isDraft = campaign.state?.toLowerCase() === 'draft';
        const isCampaignNameEditable = isDraft && !isExternal;
        const formattedCreatedAt = campaign.created_at
          ? new Date(campaign.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
          : 'Not available';
        const readOnlyInputClass = '!ring-inset !ring-transparent text-gray-900 disabled:opacity-100 disabled:text-gray-900';
        const inputInheritStyle = { fontSize: 'inherit', lineHeight: 'inherit', fontFamily: 'inherit' } as const;
        return (
          <div className="space-y-6">
            {/* Google Ads Campaign Name spans the full row at the top. */}
            <div>
              <h3 className="text-slate-800 font-semibold mb-2">Google Ads Campaign Name</h3>
              <Input
                value={draft.name ?? ''}
                onChange={isCampaignNameEditable ? (e) => updateDraft('name', e.target.value) : undefined}
                maxLength={256}
                className={readOnlyInputClass}
                style={inputInheritStyle}
                disabled={!isCampaignNameEditable}
                readOnly={!isCampaignNameEditable}
              />
            </div>

            {/* Three short metadata fields on one row. */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
              <div>
                <h3 className="text-slate-800 font-semibold mb-2">Website URL</h3>
                <Input
                  value={campaign.website_url || ''}
                  className={readOnlyInputClass}
                  style={inputInheritStyle}
                  disabled
                  readOnly
                />
              </div>
              <div>
                <h3 className="text-slate-800 font-semibold mb-2">Campaign Type</h3>
                {/* Editable on ALL campaigns (#6381 chunk F). A campaign that already has a
                    type gets the strict warning first: type drives which optimizations run.
                    On external campaigns the first confirmation is the adoption act and
                    applies without a warning; the classifier's verdict shows as a suggestion. */}
                <Select
                  value={campaign.goal && campaign.goal !== UNCLASSIFIED_GOAL ? campaign.goal : undefined}
                  onValueChange={handleTypeSelected}
                  disabled={setCampaignType.isPending}
                >
                  <SelectTrigger className="h-9 text-gray-900">
                    <SelectValue placeholder="Select a type" />
                  </SelectTrigger>
                  <SelectContent>
                    {CAMPAIGN_TYPE_CHOICES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {CAMPAIGN_TYPE_LABELS[type]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {/* #6492: the detected type is applied, so the select itself IS the
                    suggestion and a separate "Suggested: X (N% confidence)" line
                    only restated it. classification_confidence stays on the model
                    and the API; it is ours to reason about, not the user's. This
                    line remains for the window between import and classification,
                    and for a classification that failed to apply. */}
                {campaign.origin === 'external' && campaign.goal === UNCLASSIFIED_GOAL && campaign.suggested_goal && (
                  <p className="text-sm text-gray-500 mt-1">
                    Suggested: {CAMPAIGN_TYPE_LABELS[campaign.suggested_goal] ?? campaign.suggested_goal}
                  </p>
                )}
                {/* #6612: classification failed and is not being retried, so the type stays unset
                    until someone picks one. Said plainly instead of leaving an empty select. */}
                {classificationFailed(campaign, taskTracking?.external_campaign_classification) && (
                  <p className="mt-1 flex items-center gap-1 text-sm text-[#A16207]">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    The type could not be detected automatically. Select a type to continue.
                  </p>
                )}
                <ConformationModal
                  isOpen={pendingTypeChange !== null}
                  onClose={() => setPendingTypeChange(null)}
                  onConfirm={() => {
                    if (pendingTypeChange) setCampaignType.mutate(pendingTypeChange);
                    setPendingTypeChange(null);
                  }}
                  title="Change Campaign Type"
                  description={`Changing the type to ${
                    pendingTypeChange ? CAMPAIGN_TYPE_LABELS[pendingTypeChange] : ''
                  } affects how optimizations run for this campaign. Keywords, copy and budget decisions are made differently per type.`}
                  confirmText="Change Type"
                  variant="warning"
                />
              </div>
              <div>
                <h3 className="text-slate-800 font-semibold mb-2">Created On</h3>
                <Input
                  value={formattedCreatedAt}
                  className={readOnlyInputClass}
                  style={inputInheritStyle}
                  disabled
                  readOnly
                />
              </div>
            </div>

            <div>
              <h3 className="text-slate-800 font-semibold mb-2">Campaign Description</h3>
              <textarea
                value={draft.businessDescription ?? ''}
                onChange={(e) => updateDraft('businessDescription', e.target.value)}
                className="w-full text-gray-900 bg-transparent border border-slate-200 rounded-lg p-2 resize-y focus:outline-none focus:ring-2 focus:ring-[#290BD9] focus:border-transparent disabled:opacity-100 disabled:text-gray-900"
                style={inputInheritStyle}
                rows={6}
                placeholder="Describe the goal of this campaign..."
                disabled={isExternal}
                readOnly={isExternal}
              />
            </div>

            {!isExternal && (
              <div>
                <h3 className="text-slate-800 font-semibold mb-2">Tracking Template</h3>
                <Input
                  value="utm_source=google&utm_medium=cpc&utm_campaign={campaignid}&utm_term={keyword}&utm_content={creative}"
                  disabled
                  readOnly
                  className={readOnlyInputClass}
                  style={inputInheritStyle}
                />
              </div>
            )}
          </div>
        );
      })()}

      {/* === MISC / ASSETS TAB — one sub-tab per section (#4468 flattened the
              earlier Sitelinks / Descriptors / Showcase grouping). The Add button is
              hoisted into the tab row (right side) and its label + handler are dynamic
              per active sub-tab. */}
      {activeTab === 'assets' && (() => {
        // Counts shown as `N/max` badges next to each tab label. Inherited account assets are
        // left out of both: Google's per-campaign limit counts campaign-level links only, and
        // account callouts stop serving once the campaign has its own (#6353).
        const siteLinkCount = campaignWithAssets.site_links?.length ?? 0;
        const calloutCount = campaignWithAssets.callouts?.length ?? 0;
        const snippetCount = campaignWithAssets.structured_snippets?.length ?? 0;
        const hasExistingPhone = !!campaign.phone_numbers?.[0];
        const phoneCount = hasExistingPhone ? 1 : 0;
        const priceCount = campaign.prices?.length ?? 0;
        const promotionCount = campaign.promotions?.length ?? 0;

        type SubTabConfig = {
          id: MiscSubTab;
          label: string;
          count: number;
          max: number | null;
          // Asset sub-tabs carry an Add action; read-only sub-tabs (Tracking) omit these.
          addLabel?: string;
          addIcon?: 'plus' | 'pencil';
          triggerAdd?: () => void;
        };
        const subTabs: SubTabConfig[] = [
          { id: 'sitelinks', label: 'Sitelinks', count: siteLinkCount, max: 20, addLabel: 'Add Sitelink', addIcon: 'plus', triggerAdd: () => sitelinkRef.current?.openAdd() },
          { id: 'callouts', label: 'Callouts', count: calloutCount, max: 20, addLabel: 'Add Callout', addIcon: 'plus', triggerAdd: () => calloutRef.current?.openAdd() },
          { id: 'snippets' as MiscSubTab, label: 'Snippets', count: snippetCount, max: 4, addLabel: 'Add Snippet', addIcon: 'plus' as const, triggerAdd: () => snippetRef.current?.openAdd() },
          { id: 'prices' as MiscSubTab, label: 'Prices', count: priceCount, max: 8, addLabel: 'Add Price', addIcon: 'plus' as const, triggerAdd: () => priceRef.current?.openAdd() },
          { id: 'promotions' as MiscSubTab, label: 'Promotions', count: promotionCount, max: 20, addLabel: 'Add Promotion', addIcon: 'plus' as const, triggerAdd: () => promotionRef.current?.openAdd() },
          { id: 'phone' as MiscSubTab, label: 'Calls', count: phoneCount, max: 1, addLabel: 'Add Number', addIcon: 'plus' as const, triggerAdd: () => phoneRef.current?.openAdd() },
          // Image extensions are pre-GA (#4376) — sub-tab only appears in test mode. The
          // section manages its own attach flow, so no hoisted Add button.
          ...(isTestMode ? [{ id: 'images' as MiscSubTab, label: 'Images', count: 0, max: null }] : []),
          { id: 'tracking' as MiscSubTab, label: 'Tracking', count: 0, max: null },
        ];
        // If test mode flipped off while on a hidden tab, fall back to a visible tab.
        const activeSubTab = subTabs.some((t) => t.id === miscSubTab) ? miscSubTab : 'sitelinks';
        const activeConfig = subTabs.find((t) => t.id === activeSubTab);
        const isAddDisabled = isExternal || (activeConfig?.max != null && activeConfig.count >= activeConfig.max);

        return (
          <div className="space-y-6">
            {/* Underlined sub-tabs with hoisted Add button on the right. -mt-2.5 keeps the space
                above the tab text even with the space below the divider (#4812). */}
            <div className="-mt-2.5 border-b border-gray-200 flex items-end justify-between">
              <div className="flex gap-0">
                {subTabs.map((tab) => {
                  const isActive = activeSubTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => onAssetsSubTabChange(tab.id)}
                      className={`px-5 py-2.5 text-sm font-medium transition-all border-b-2 -mb-px ${
                        isActive
                          ? 'border-[#290BD9] text-gray-900'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>
              {!isExternal && activeConfig?.triggerAdd && (
                <div className="pb-1.5">
                  <Button
                    variant="outline"
                    className="text-gray-900"
                    onClick={activeConfig.triggerAdd}
                    disabled={isAddDisabled}
                  >
                    {activeConfig.addIcon === 'pencil' ? (
                      <Pencil className="h-4 w-4 mr-2" />
                    ) : (
                      <Plus className="h-4 w-4 mr-2" />
                    )}
                    {activeConfig.addLabel}
                  </Button>
                </div>
              )}
            </div>

            {activeSubTab === 'sitelinks' && (
              <SitelinkSection ref={sitelinkRef} campaign={campaignWithAssets} disabled={isExternal} />
            )}
            {activeSubTab === 'callouts' && (
              <CalloutSection ref={calloutRef} campaign={campaignWithAssets} disabled={isExternal} />
            )}
            {activeSubTab === 'snippets' && (
              <StructuredSnippetSection ref={snippetRef} campaign={campaignWithAssets} disabled={isExternal} />
            )}
            {activeSubTab === 'phone' && (
              <PhoneNumberSection ref={phoneRef} campaign={campaignWithAssets} disabled={isExternal} />
            )}
            {activeSubTab === 'prices' && (
              <PriceSection ref={priceRef} campaign={campaignWithAssets} disabled={isExternal} />
            )}
            {activeSubTab === 'promotions' && (
              <PromotionSection ref={promotionRef} campaign={campaignWithAssets} disabled={isExternal} />
            )}
            {activeSubTab === 'images' && isTestMode && (
              <ImageSection campaign={campaignWithAssets} disabled={isExternal} />
            )}
            {activeSubTab === 'tracking' && <TrackingTemplateView campaignId={campaign.id} />}
          </div>
        );
      })()}

      {/* === BUDGET TAB === */}
      {activeTab === 'budget' && !organizationData?.default_currency && (
        <Card className="p-12 flex flex-col items-center text-center justify-center min-h-[320px]">
          <AlertCircle className="h-10 w-10 mb-4 text-[#B91C1C] opacity-60" strokeWidth={1} />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Set Your Currency First</h3>
          <p className="text-sm text-gray-500 max-w-md mb-6">
            Set your organisation currency before configuring campaign budgets.
          </p>
          <a href={`/organizations/${organizationData?.id}#overview`} className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#290BD9] hover:bg-[#2309C5] text-white text-sm font-medium rounded-md transition-colors">
            Go to Organisation Settings
            <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </Card>
      )}
      {activeTab === 'budget' && !!organizationData?.default_currency && (() => {
        const budgetSubTabs: { id: BudgetSubTab; label: string }[] = [
          { id: 'budget-bidding', label: 'Budget & Bidding' },
          { id: 'bid-adjustments', label: 'Bid adjustments' },
          { id: 'schedule', label: 'Scheduling' },
        ];
        return (
          <div className="space-y-6">
            {/* Sub-tab nav — same pattern as the Misc/Assets sub-tabs. -mt-2.5 keeps the space above
                the tab text even with the space below the divider (#4812). */}
            <div className="-mt-2.5 border-b border-gray-200">
              <div className="flex gap-0">
                {budgetSubTabs.map((tab) => {
                  const isActive = budgetSubTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setBudgetSubTab(tab.id)}
                      className={`px-5 py-2.5 text-sm font-medium transition-all border-b-2 -mb-px ${
                        isActive
                          ? 'border-[#290BD9] text-gray-900'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                      data-testid={`budget-subtab-${tab.id}`}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {budgetSubTab === 'budget-bidding' && (
              <div className="flex gap-6">
                <div className="w-1/2">
                  <CampaignBudgetView
                    budget={draft.budget}
                    setBudget={(value) => updateDraft('budget', value)}
                    isBudgetApproved={campaign.budget_state === APPROVAL_STATE.APPROVED}
                    onManageBudget={() => setIsBudgetModalOpen(true)}
                    onGetRecommendation={() => getBudgetRecommendation(campaign.id).then(r => r.budget)}
                    remainingDailyBudget={remainingDailyBudget}
                    savedBudget={campaign.budget}
                    disabled={isExternal}
                    hasBeenLive={!!campaign.first_published_at}
                  />
                  <Dialog open={isBudgetModalOpen} onOpenChange={setIsBudgetModalOpen}>
                    <DialogContent className="sm:max-w-[1100px] max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
                      <DialogHeader>
                        <DialogTitle>Manage Budget</DialogTitle>
                      </DialogHeader>
                      <BudgetV2 highlightCampaignId={campaign.id} />
                    </DialogContent>
                  </Dialog>
                </div>
                <div className="w-1/2">
                  <BiddingStrategySelector
                    biddingStrategyType={draft.biddingStrategyType as BiddingStrategyType}
                    setBiddingStrategyType={(strategy) => {
                      updateDraft('biddingStrategyType', strategy);
                      if (strategy === 'MAXIMIZE_CONVERSIONS') {
                        updateDraft('targetRoas', undefined);
                      } else if (strategy === 'MAXIMIZE_CONVERSION_VALUE') {
                        updateDraft('targetRoas', (campaign.target_roas != null && campaign.target_roas > 0) ? campaign.target_roas : 1.0);
                      } else if (strategy === 'TARGET_SPEND') {
                        updateDraft('targetRoas', undefined);
                        updateDraft('targetCpa', 0);
                      }
                    }}
                    targetRoas={draft.targetRoas}
                    setTargetRoas={(value) => updateDraft('targetRoas', value)}
                    targetCpa={draft.targetCpa}
                    setTargetCpa={(value) => updateDraft('targetCpa', value)}
                    conversionTrackingEnabled={conversionTrackingEnabled}
                    disabled={isExternal}
                  />
                </div>
              </div>
            )}

            {budgetSubTab === 'schedule' && (
              <AdScheduleView
                campaignId={campaign.id}
                disabled={isExternal}
              />
            )}

            {budgetSubTab === 'bid-adjustments' && (
              <BidAdjustmentsView
                campaignId={campaign.id}
                targetLocations={campaign.target_locations || []}
                disabled={isExternal}
                businessId={campaign.business_id ?? null}
                biddingStrategyType={draft.biddingStrategyType ?? campaign.bidding_strategy_type ?? null}
              />
            )}
          </div>
        );
      })()}

      {/* === NEGATIVES TAB === */}
      {activeTab === 'negatives' && (
        <div className="space-y-6">
          <CampaignNegativeKeywordsView
            campaignId={campaign.id}
            disabled={isExternal}
          />
        </div>
      )}

      {/* === CONVERSIONS TAB === */}
      {activeTab === 'conversions' && (
        <div className="space-y-6">
          <ConversionTrackingTab campaignId={campaign.id} disabled={isExternal} />
        </div>
      )}

      {/* === TARGETING TAB === */}
      {activeTab === 'targeting' && (() => {
        const targetingSubTabs: { id: TargetingSubTab; label: string }[] = [
          { id: 'locations', label: 'Locations' },
          { id: 'languages', label: 'Languages' },
          { id: 'mode', label: 'Presence' },
          { id: 'excluded', label: 'Excluded Locations' },
          { id: 'ip-exclusions', label: 'IP Exclusions' },
        ];
        return (
          <div className="space-y-6">
            {/* Underlined sub-tabs (matches Misc/Budget). -mt-2.5 keeps the space above
                the tab text even with the space below the divider (#4812). */}
            <div className="-mt-2.5 border-b border-gray-200">
              <div className="flex gap-0">
                {targetingSubTabs.map((tab) => {
                  const isActive = targetingSubTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setTargetingSubTab(tab.id)}
                      className={`px-5 py-2.5 text-sm font-medium transition-all border-b-2 -mb-px ${
                        isActive
                          ? 'border-[#290BD9] text-gray-900'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {targetingSubTab === 'locations' && (
              <CampaignLocationsSection
                locations={draft.locations}
                setLocations={(locations) => updateDraft('locations', locations)}
                currentTargets={campaign.proximity_targets ?? []}
                proximityValue={draft.proximityTargets}
                onProximityChange={(t) => updateDraft('proximityTargets', t)}
                businessId={business?.id}
                disabled={isExternal}
                campaignName={campaign.name}
                excludedCriteriaIds={excludedCriteriaIds}
              />
            )}
            {targetingSubTab === 'excluded' && (
              <ExcludedLocationsSection
                excludedLocations={draft.excludedLocations ?? []}
                onChange={(next) => updateDraft('excludedLocations', next)}
                disabled={isExternal}
                businessId={business?.id}
              />
            )}
            {targetingSubTab === 'mode' && <LocationTargetingModeSection campaign={campaign} />}
            {targetingSubTab === 'languages' && (
              <LanguageSection campaign={campaignWithAssets} disabled={isExternal} />
            )}
            {/* #4643: IP Exclusions moved into Targeting from its own tab. */}
            {targetingSubTab === 'ip-exclusions' && (
              <CampaignIpExclusionsView campaignId={campaign.id} disabled={isExternal} />
            )}
          </div>
        );
      })()}

      {/* === LANDING PAGE TAB (#5701) === */}
      {activeTab === 'landing-page' && (
        <div className="space-y-6">
          <LandingPageTab campaign={campaign} />
        </div>
      )}
    </div>
  );
}
